import React from "react";
import type { HistoryBackedStateOptions } from "@/main/react/page/useHistoryBackedState";
import { Localizer } from "@/main/react/Types";
import { MetaHandler } from "@/main/common/MetaHandler";
import { getBoundedCacheEntry, setBoundedCacheEntry } from "@/main/react/page/search/searchCache";
import { dedupeItems } from "@/main/react/page/search/searchUtils";
import SearchResultSkeleton from "@/main/react/page/search/result-row/SearchResultSkeleton";
import SearchPagination from "@/main/react/page/search/SearchPagination";
import SearchEmptyState, { SearchZeroMatchActions, UnresolvedPeopleSearchActions } from "@/main/react/page/search/SearchEmptyState";
import SearchErrorState from "@/main/react/page/search/SearchErrorState";
import SearchBlendDivider from "@/main/react/page/search/SearchBlendDivider";
import SearchZeroMatchShelf, { type SearchZeroMatchShelfOptions } from "@/main/react/page/search/SearchZeroMatchShelf";
import { getFriendlyCountBadgeLabel } from "@/main/react/card/CardMetadataBadgeCopy";
import { queueSearchFillerRowClicked, trackSearchBlendedPageShown } from "@/main/analytics/analyticsEvents";
import { PaginatedListSsrFallback, SsrRestoredList } from "@/main/react/list/ssrFallbacks";
import { DESTRUCTIVE_NOTICE_SURFACE_CLASS } from "@/main/react/component/destructiveNoticeSurface";

type CachedListState<T> = {
    rawPages: T[][];
    nextCursor?: string;
    hasMoreRaw: boolean;
    errorMessage: string | null;
};

const MAX_SEARCH_LIST_CACHE_ENTRIES = 20;
const searchListCache = new Map<string, CachedListState<any>>();

function getOrCreateCache<T>(cacheKey: string): CachedListState<T> {
    const existing = getBoundedCacheEntry(searchListCache, cacheKey);
    if (existing) {
        return existing as CachedListState<T>;
    }

    const created: CachedListState<T> = {
        rawPages: [],
        nextCursor: undefined,
        hasMoreRaw: true,
        errorMessage: null,
    };
    return setBoundedCacheEntry(searchListCache, cacheKey, created, MAX_SEARCH_LIST_CACHE_ENTRIES);
}

export function resetSearchPaginatedListCacheForTests(): void {
    searchListCache.clear();
}

/**
 * Pre-populates a list's first page so a freshly-mounted {@link SearchPaginatedList} renders without
 * an initial fetch. Used by the implicit-search tab fallback: the backend resolve endpoint already
 * returns the first page of the tab it picked, so we seed that page here and let pagination beyond
 * page 1 use the normal per-type fetcher. Cursor/hasMore semantics mirror {@link ensurePageLoaded}.
 */
export function seedSearchPaginatedListCache<T extends { paginationParameter?: string | null }>(
    cacheKey: string,
    items: T[],
): void {
    const normalized = Array.isArray(items) ? items.filter(Boolean) : [];
    const lastItem = normalized[normalized.length - 1];
    const nextCursor = lastItem?.paginationParameter ?? undefined;
    const seeded: CachedListState<T> = {
        rawPages: normalized.length > 0 ? [normalized] : [],
        nextCursor,
        hasMoreRaw: Boolean(normalized.length > 0 && nextCursor != null),
        errorMessage: null,
    };
    setBoundedCacheEntry(searchListCache, cacheKey, seeded, MAX_SEARCH_LIST_CACHE_ENTRIES);
}

export function SearchPaginatedList<T extends { paginationParameter?: string | null }>(props: {
    cacheKey: string;
    query: string;
    page: number;
    pageSize?: number;
    localizer: Localizer;
    resultLabel: string;
    fetchPage: (cursor?: string) => Promise<T[]>;
    getId: (item: T) => string | undefined;
    // `position` is the 1-based ABSOLUTE slot across pages, not the index within this page. Only this
    // component can compute it — it owns `page` and `pageSize` — so it is handed down rather than left
    // for each caller to re-derive from state it does not have.
    renderItem: (item: T, position: number) => React.ReactNode;
    // Fired once per RESOLVED page, for a vertical that wants an impression denominator
    // (`feed_page_shown`). Deduped by `cacheKey:page` in the same shape as the
    // `trackSearchBlendedPageShown` guard further down, so a re-render cannot re-fire it. Paging away
    // and back IS a new impression — the reader was shown the page again.
    onPageShown?: (info: { pageIndex: number; pageItems: T[] }) => void;
    // Real-match count for the active vertical (the tab badge). On a sparse/blended terminal page the
    // first `strongMatchCount` rows are the real matches and the rest are "more you might like" filler;
    // this is where the soft divider is placed. `null`/`undefined` (unknown yet) => no divider.
    strongMatchCount?: number | null;
    // Singular vertical name (discussion|argument|group|series|user) for the blended-search analytics
    // events emitted from this list; the generic list is otherwise blind to which vertical it renders.
    analyticsEntityType?: string;
    skeletonCount?: number;
    showAvatarSkeleton?: boolean;
    // `options.replace` marks a page change this list CORRECTED rather than one the user asked for, so
    // the owner can replace the history entry instead of pushing. Without it an out-of-range deep link
    // (`?page=9` on a three-page result) silently costs the user a Back press to escape a page they
    // never navigated to. User-driven paging omits it and keeps pushing.
    onPageChange: (page: number, options?: HistoryBackedStateOptions) => void;
    /**
     * A real URL for page N, forwarded verbatim to the pager (`DISCOVERY-26`). Supplied by `/search`,
     * whose page index lives in the URL; omitted by hubs whose page index is component state, where
     * there is nothing to link to. See {@link SearchPagination}'s own note.
     */
    buildPageHref?: (page: number) => string;
    onMetaChange?: (meta: {
        loadedCount: number;
        hasMore: boolean;
        isLoading: boolean;
        errorMessage: string | null;
    }) => void;
    onClearFilters?: () => void;
    onTryShorterQuery?: () => void;
    /**
     * Opens the composer seeded with the failed query (`DISCOVERY-19`, `EMPTY-STATES-103`, D20). Like
     * the two props above, this generic list only forwards it: the owning page decides which verticals
     * it applies to and how the query reaches the composer.
     */
    onWriteAboutQuery?: () => void;
    // The two "we couldn't find that person" actions (design §3.2), forwarded verbatim to the empty
    // state. This generic list serves every vertical and knows nothing about tabs or sessions, so it
    // does not decide when they apply — the owning page supplies them only for the users vertical, and
    // only when a session exists.
    unresolvedPeopleSearch?: UnresolvedPeopleSearchActions;
    /**
     * Present ⇒ mount the typeahead's topic/group shelf on the zero-match branches (`DISCOVERY-72`).
     *
     * Like the three props above, this generic list only forwards it. The owning page decides which
     * verticals get a shelf and whether that vertical's shelf carries groups; a hub passes nothing and
     * is unchanged.
     */
    zeroMatchShelf?: SearchZeroMatchShelfOptions;
    /**
     * True when EVERY vertical's count is zero, not just this one's. Only the owning page can know it
     * (it holds the tab counts), and it changes what the empty state may say: the default body copy
     * routes the reader to "the other tabs", which on an all-zero query sends them to tabs the page is
     * already holding a zero for. `EMPTY-STATES-11`.
     */
    allVerticalsEmpty?: boolean;
    /**
     * True while the count that decides the zero-match branch is genuinely IN FLIGHT (`DISCOVERY-71`).
     *
     * The list and the tab counts are two independent requests. `strongMatchCount` is `null` until the
     * counts land and `null` fails the verdict gate, so a list that resolved first painted six filler
     * cards under a strip of shimmer placeholders with nothing on screen saying the search matched
     * nothing — a page that looks exactly like a search that succeeded. The counts then arrive and a
     * ~238 CSS px block is inserted ABOVE the rows, moving everything a reader was already reaching for.
     *
     * `strongMatchCount` alone cannot express this: `null` there means both "in flight" and "this
     * caller reports no counts at all" (`/tag/*` and every other hub), and holding a hub's first paint
     * on a count that will never arrive would hang it on skeletons forever. Only the owning page knows
     * the difference, so it is a separate, explicitly-true-only signal.
     */
    strongMatchCountPending?: boolean;
    /**
     * A hub-specific empty state, replacing the shared `/search` one. `SearchEmptyState` serves every
     * vertical and every hub, so a hub that is not a search (`/tag/*`) cannot be served by another
     * branch on the query string: with `query=""` it falls through to a panel headed "Search results",
     * reporting the failure of a search nobody ran (`DISCOVERY-5`). The owning page supplies its own.
     */
    emptyState?: React.ReactNode;
    /**
     * The server-rendered list this island mounted over, restored beneath the error state when the
     * fetch fails terminally. Omit on `/search` itself — there is no SSR list behind a query — and set
     * it on every hub that has one. See `ssrFallbacks.tsx` and AGENTS.md rule 32.
     */
    ssrFallback?: PaginatedListSsrFallback;
}) {
    const {
        cacheKey,
        query,
        page,
        pageSize = 20,
        localizer,
        resultLabel,
        fetchPage,
        getId,
        renderItem,
        onPageShown,
        strongMatchCount,
        analyticsEntityType,
        skeletonCount = 5,
        showAvatarSkeleton = false,
        onPageChange,
        onMetaChange,
        onClearFilters,
        onTryShorterQuery,
        onWriteAboutQuery,
    } = props;

    const loadingRef = React.useRef(false);
    const [revision, setRevision] = React.useState(0);
    const [isLoading, setIsLoading] = React.useState(false);

    const refresh = React.useCallback(() => {
        setRevision((value) => value + 1);
    }, []);

    const deriveVisibleItems = React.useCallback(() => {
        const cache = getOrCreateCache<T>(cacheKey);
        const flattened = cache.rawPages.flat();
        return dedupeItems(flattened, getId);
    }, [cacheKey, getId]);

    const ensurePageLoaded = React.useCallback(async () => {
        const cache = getOrCreateCache<T>(cacheKey);
        if (loadingRef.current) {
            return;
        }

        loadingRef.current = true;
        setIsLoading(true);
        cache.errorMessage = null;

        try {
            while (true) {
                const visibleItems = dedupeItems(cache.rawPages.flat(), getId);
                const requiredItems = page * pageSize;

                if (visibleItems.length >= requiredItems || !cache.hasMoreRaw) {
                    break;
                }

                const response = await fetchPage(cache.rawPages.length > 0 ? cache.nextCursor : undefined);
                const normalized = Array.isArray(response) ? response.filter(Boolean) : [];
                cache.rawPages.push(normalized);

                const lastItem = normalized[normalized.length - 1];
                cache.nextCursor = lastItem?.paginationParameter ?? undefined;
                cache.hasMoreRaw = Boolean(normalized.length > 0 && cache.nextCursor != null);

                if (normalized.length === 0) {
                    cache.hasMoreRaw = false;
                    break;
                }
            }
        } catch (error) {
            cache.errorMessage = error instanceof Error ? error.message : String(error);
        } finally {
            loadingRef.current = false;
            setIsLoading(false);
            refresh();
        }
    }, [cacheKey, fetchPage, getId, page, pageSize, refresh]);

    React.useEffect(() => {
        void ensurePageLoaded();
    }, [ensurePageLoaded]);

    const visibleItems = React.useMemo(() => deriveVisibleItems(), [deriveVisibleItems, revision]);
    const maxLoadedPage = Math.max(1, Math.ceil(visibleItems.length / pageSize));
    const currentPageItems = visibleItems.slice((page - 1) * pageSize, page * pageSize);
    const cache = getOrCreateCache<T>(cacheKey);
    const hasNextPage = visibleItems.length > page * pageSize || cache.hasMoreRaw;
    const hasPreviousPage = page > 1;
    const availablePages = Array.from({ length: Math.max(1, maxLoadedPage) }, (_, index) => index + 1);

    /**
     * `DISCOVERY-71` — the verdict may not arrive AFTER the rows it is a verdict about.
     *
     * The hold is bounded three ways so it can never become a hang: it applies only while the owning
     * page reports the count request is genuinely in flight (which always settles, in both the `then`
     * and the `catch`), only on page 1, and only BEFORE this list has ever painted rows. The last of
     * those is what keeps a later count refresh — a language switch, whose counts are re-requested from
     * `onMetaChange` after the list settles — from replacing rows the reader is already looking at with
     * skeletons. Nothing about the data flow changes: `ensurePageLoaded` and `onMetaChange` both run
     * from effects, so the request that resolves the count still fires while the skeletons are up.
     */
    const hasPaintedRowsRef = React.useRef(false);
    const isFetchingFirstPage = isLoading && cache.rawPages.length === 0;
    const isHoldingForVerdict = props.strongMatchCountPending === true
        && page === 1
        && !hasPaintedRowsRef.current;
    const isInitialLoad = isFetchingFirstPage || isHoldingForVerdict;
    React.useEffect(() => {
        if (!isInitialLoad && visibleItems.length > 0) {
            hasPaintedRowsRef.current = true;
        }
    });

    // On a sparse/blended terminal page (blend nulls every cursor => no next page, page 1), the first
    // `strongMatchCount` rows are the real matches and the remainder is "more you might like" filler.
    // Splice a soft divider at that seam. No divider when the page is non-terminal (abundant — real
    // matches fill the page and there is a page 2), or when every row is a real match (nothing below
    // the seam).
    //
    // ZERO IS A VALID SEAM INDEX, and excluding it was the bug. `strongMatchCount > 0` meant that the
    // one case where the page most needs to declare itself — a query that matched NOTHING, so every
    // row is filler — was the single case that rendered no divider at all. The result was a full page
    // of unrelated results presented exactly like a real result set, with nothing anywhere on screen
    // saying the query matched nothing. A divider at index 0 puts the label above every row, which is
    // precisely what "none of these are matches" looks like.
    const blendDividerIndex =
        !hasNextPage
        && page === 1
        && typeof strongMatchCount === "number"
        && strongMatchCount >= 0
        && strongMatchCount < currentPageItems.length
            ? strongMatchCount
            : null;

    // `null` at zero or when counts have not resolved, so the line is absent rather than claiming 0.
    const totalResultsLabel = getFriendlyCountBadgeLabel(
        localizer,
        "search.suggestions.results_count",
        typeof strongMatchCount === "number" ? strongMatchCount : undefined,
    );

    // The impression denominator for the discussions vertical. Driven by page RESOLUTION (the load has
    // finished and this page's rows exist), never by render, and deduped by cache-key+page so a
    // re-render or a StrictMode double-invoke cannot inflate it. An empty result is still a real
    // impression — the reader was shown an empty page — so `rawPages.length > 0` rather than
    // `currentPageItems.length > 0` is what gates it.
    const pageShownFiredKeyRef = React.useRef<string | null>(null);
    const onPageShownRef = React.useRef(onPageShown);
    React.useEffect(() => {
        onPageShownRef.current = onPageShown;
    }, [onPageShown]);
    React.useEffect(() => {
        const report = onPageShownRef.current;
        if (!report || isLoading || cache.rawPages.length === 0) {
            return;
        }

        const firedKey = `${cacheKey}:${page}`;
        if (pageShownFiredKeyRef.current === firedKey) {
            return;
        }
        pageShownFiredKeyRef.current = firedKey;

        try {
            report({ pageIndex: page, pageItems: currentPageItems });
        } catch {
            // Instrumentation is side-effect-only; it may never break the results list.
        }
        // `currentPageItems` is recomputed every render by design; the ref guard above is what makes
        // this fire once, so it is deliberately not a dependency.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cacheKey, page, isLoading, cache.rawPages.length]);

    // Fire the "blended page shown" signal once per blended terminal page (deduped by cache-key+page),
    // not on every re-render. Analytics is side-effect-only; trackEvent swallows its own failures.
    const blendShownFiredKeyRef = React.useRef<string | null>(null);
    React.useEffect(() => {
        if (blendDividerIndex === null) {
            return;
        }
        const firedKey = `${cacheKey}:${page}`;
        if (blendShownFiredKeyRef.current === firedKey) {
            return;
        }
        blendShownFiredKeyRef.current = firedKey;
        trackSearchBlendedPageShown({
            entityType: analyticsEntityType,
            strongMatchCount: strongMatchCount ?? 0,
        });
    }, [blendDividerIndex, cacheKey, page, analyticsEntityType, strongMatchCount]);

    React.useEffect(() => {
        onMetaChange?.({
            loadedCount: visibleItems.length,
            hasMore: hasNextPage,
            isLoading,
            errorMessage: cache.errorMessage,
        });
    }, [cache.errorMessage, hasNextPage, isLoading, onMetaChange, visibleItems.length]);

    React.useEffect(() => {
        if (page <= maxLoadedPage) {
            return;
        }

        if (hasNextPage) {
            return;
        }

        // A correction, not a navigation: the user deep-linked past the last page. Replace the entry so
        // Back leaves the site rather than returning to the out-of-range page we just took them off.
        onPageChange(Math.max(1, maxLoadedPage), { replace: "canonicalize" });
    }, [hasNextPage, maxLoadedPage, onPageChange, page]);

    /**
     * The always-mounted live region (`DISCOVERY-38`), now built ONCE and rendered by every branch
     * (`DISCOVERY-65`).
     *
     * It used to live inside the main return only, i.e. AFTER both early returns — so on the `/tag/*`
     * hub, which always supplies its own `emptyState` and therefore always takes the empty branch,
     * there was no live region in the accessibility tree at all. That is the state where feedback
     * matters most: the "Your languages" select is the page's only control, its whole job is to change
     * what is below it, and a screen-reader or magnifier user toggling it received nothing at all —
     * no count, no "no results", no focus move.
     *
     * The two constraints this component states about the region are kept: it is NOT inside the
     * `space-y-4` container (that would hand every row a 16 px margin it never had), and it does NOT
     * use `empty:hidden` (`display: none` removes an element from the accessibility tree, which makes
     * an "always-mounted" region exactly as invisible as no region).
     *
     * Its contents on the empty and settled branches are a visually-hidden status, so the region has
     * something to announce when the scope or the page changes rather than only when the zero-match
     * verdict fires. Both strings already ship in all 25 catalogs; no key is minted.
     *
     * ⚠️ Still NOT proven, and the same limit `DISCOVERY-38` recorded: jsdom has no announcement model,
     * so the guards can assert the MECHANISM (a region in the tree before the contents change) and
     * never the announcement itself.
     */
    // `strongMatchCount` first, `visibleItems.length` only as the fallback for a caller that reports no
    // count at all (`/tag/*` and every other hub). The two differ whenever a sparse page is blend-filled,
    // and the visible `search-total-results` line below already announces the REAL matches — an
    // announcement that disagreed with the line beside it would be worse than none.
    const hasTerminalError = Boolean(cache.errorMessage) && visibleItems.length === 0 && !isInitialLoad;
    const announcedResultCount = typeof strongMatchCount === "number" ? strongMatchCount : visibleItems.length;
    const settledCountLabel = getFriendlyCountBadgeLabel(
        localizer,
        "search.suggestions.results_count",
        announcedResultCount,
    );
    const liveRegion = (
        <div
            role="status"
            aria-live="polite"
            data-playwright-id="search-zero-match-region"
        >
            {blendDividerIndex === 0 && strongMatchCount === 0 ? (
                <SearchBlendDivider
                    localizer={localizer}
                    strongMatchCount={0}
                    // `DISCOVERY-51` — the divider cannot tell "this tab is empty" from "the whole site
                    // is empty"; only the owning page can, and it has always computed the flag.
                    allVerticalsEmpty={props.allVerticalsEmpty}
                    className="mb-4"
                />
            ) : isInitialLoad || hasTerminalError ? null : (
                // Silent while loading, and silent on the terminal-error branch: "No results" is a
                // statement about the corpus and a failed fetch is not evidence for it. The error state
                // below says what actually happened, and it says it in the page, not only to a reader
                // who happens to be listening.
                <span className="sr-only" data-playwright-id="search-list-status">
                    {settledCountLabel ?? localizer("search.no_results")}
                </span>
            )}
        </div>
    );

    if (hasTerminalError) {
        // Rule 32: on a hub whose HTML already carried a crawlable list, this branch used to leave a
        // rendering crawler with nothing but a retry button — `createRoot()` discarded the list at
        // mount. Restore it beneath the error state (a `/tag/*` hub exists only for SEO, so this is the
        // sharpest case of the six). `SearchSsrRestore` renders nothing when there is no snapshot, so
        // `/search` — which passes no `ssrFallback` — is unchanged.
        return (
            <>
                {liveRegion}
                <SearchErrorState
                    query={query}
                    localizer={localizer}
                    onRetry={() => {
                        const retryCache = getOrCreateCache<T>(cacheKey);
                        retryCache.errorMessage = null;
                        void ensurePageLoaded();
                    }}
                />
                <SsrRestoredList componentName={props.ssrFallback?.componentName} selector={props.ssrFallback?.selector} />
            </>
        );
    }

    if (!isInitialLoad && visibleItems.length === 0) {
        // A hub that is not a search supplies its own. See the `emptyState` prop.
        return (
            <>
                {liveRegion}
                {props.emptyState !== undefined ? (
                    props.emptyState
                ) : (
                    <SearchEmptyState
                        query={query}
                        localizer={localizer}
                        onClearFilters={onClearFilters}
                        onTryShorterQuery={onTryShorterQuery}
                        onWriteAboutQuery={onWriteAboutQuery}
                        unresolvedPeopleSearch={props.unresolvedPeopleSearch}
                        allVerticalsEmpty={props.allVerticalsEmpty}
                        zeroMatchShelf={props.zeroMatchShelf}
                    />
                )}
            </>
        );
    }

    return (
        // The root is a bare wrapper, NOT the `space-y-4` container, and that is load-bearing. The
        // live region below has to sit above the rows; as a first child of a `space-y-4` container it
        // would hand the rows a 16px `margin-top` they never had, on every search page, empty region
        // or not. An empty region in a plain wrapper has zero height and contributes nothing, and the
        // notice supplies its own `mb-4` when it is there.
        <div>
            {/* `DISCOVERY-38` — the zero-match verdict's live region, ALWAYS MOUNTED. It used to be the
              * notice element itself, which existed only when `strongMatchCount === 0` and was mounted
              * inside the mapped result list, so the node and its text entered the DOM together after
              * the fetch resolved — the construction `SearchResultsPage.tsx` states in its own comment
              * is not reliably announced. It is now built once above every branch of this component
              * (`DISCOVERY-65`); see the `liveRegion` definition for the full rationale and its two
              * standing constraints. */}
            {liveRegion}

            {/* `DISCOVERY-72` — the typeahead's topic/group shelf, ABOVE the actions and BELOW the
              * verdict.
              *
              * The order is adjudicated, not incidental: **verdict → shelf → actions → filler rows**
              * (wave-19 re-adjudication §1.1). The shelf is the answer that is both on-subject and free
              * — the one thing an anonymous stranger can act on without an account — so it outranks
              * write-a-post, which the same decision demoted to secondary; and it sits under the
              * verdict because it only makes sense once the reader knows why it is there.
              *
              * OUTSIDE the live region, for the same reason the actions are: links inside a
              * `role="status"` join every announcement (`DISCOVERY-38`).
              *
              * It is NOT a filler row and must never be counted as one. `SearchPaginatedList.spec.tsx`
              * asserts the actions block leads every FILLER row, with the shelf ranked above both —
              * that re-scope was decided in writing in §1.1 before this shipped. */}
            {props.zeroMatchShelf && blendDividerIndex === 0 && strongMatchCount === 0 ? (
                <SearchZeroMatchShelf
                    className="mb-4"
                    query={query}
                    localizer={localizer}
                    options={props.zeroMatchShelf}
                />
            ) : null}

            {/* `EMPTY-STATES-67` — the recovery and authorship controls on the BLENDING branch.
              *
              * The blend guarantees six filler rows, so `visibleItems.length === 0` is never true on
              * Posts / Groups / Series and `SearchEmptyState` — which is where these controls used to
              * live — is unreachable there. The identical zero therefore produced a page with no
              * control acting on the failed query anywhere, and a page with two, depending only on
              * which tab you happened to be on.
              *
              * OUTSIDE the live region above, deliberately. That region exists to announce the verdict
              * sentence (`DISCOVERY-38`); buttons placed inside it would join every announcement and
              * an assistive-technology user would hear the controls read back on each count change.
              *
              * Gated on exactly the same condition as the verdict, so the controls cannot appear
              * without the sentence that explains them. The filler list below is untouched: the
              * never-empty blend is a defended feature and this is what sits ABOVE it. */}
            {blendDividerIndex === 0 && strongMatchCount === 0 ? (
                <SearchZeroMatchActions
                    className="mb-4"
                    query={query}
                    localizer={localizer}
                    onClearFilters={onClearFilters}
                    onTryShorterQuery={onTryShorterQuery}
                    onWriteAboutQuery={onWriteAboutQuery}
                    align="left"
                />
            ) : null}

            <div className="space-y-4">
            {isInitialLoad ? (
                <div className="space-y-3">
                    {Array.from({ length: skeletonCount }, (_, index) => (
                        <SearchResultSkeleton key={index} showAvatar={showAvatarSkeleton} />
                    ))}
                </div>
            ) : (
                <div className="space-y-3">
                    {/* `DISCOVERY-57` — the verdict has to REACH the column it is a verdict about.
                      *
                      * At 412 px the actions band ends at CSS y 494 of a 2,030 px document, so from
                      * there to the bottom — 697 CSS px, 59% of the whole scroll range — every pixel on
                      * screen is a filler card and nothing said so. A reader who arrives mid-scroll, or
                      * who scrolls back after tapping into a card and returning, sees six confident
                      * `bg-card` rows with authors and engagement counts while every tab above reads 0.
                      *
                      * STICKY rather than a second block at the top: "said once, at the top" is exactly
                      * what the page already did. Pinned just under the fixed app header, the label
                      * stays on screen for the whole of the column it names.
                      *
                      * Two recorded constraints are respected. It is NOT the blend strip — `DISCOVERY-3`
                      * requires that strip to be ABSENT at zero, and this carries no hairlines and its
                      * own `data-playwright-id`. And it does not touch the cards: the never-empty blend
                      * is a defended feature and the filler must stay inviting to click, so the label
                      * gains presence rather than the suggestions losing it.
                      *
                      * No key minted: `search.blend_divider.more` ships in all 25 catalogs and is the
                      * exact sentence the `> 0` separator uses for these same rows. */}
                    {blendDividerIndex === 0 && strongMatchCount === 0 ? (
                        <p
                            className="sticky top-[calc(4.5rem+env(safe-area-inset-top)+var(--seavien-connection-banner-height,0px))] z-10 bg-[var(--background1)]/95 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground backdrop-blur"
                            data-playwright-id="search-filler-column-label"
                        >
                            {localizer("search.blend_divider.more")}
                        </p>
                    ) : null}
                    {currentPageItems.map((item, index) => {
                        // Rows at/below the divider are "more you might like" filler; a click on one is
                        // the discovery signal. Capture-phase so it fires before the row's own link
                        // navigates. Analytics is side-effect-only and never blocks the navigation.
                        //
                        // ⚠️ Capture phase buys ORDERING WITHIN THE DISPATCH and nothing about DELIVERY,
                        // which is what this comment used to imply and is why the defect survived: GTM
                        // reads `dataLayer` asynchronously, so an immediate push made microseconds before
                        // the document is torn down is discarded regardless of which phase produced it.
                        // Hence `queueSearchFillerRowClicked` — matching `queueSearchResultClicked` on
                        // the strong-match rows in this same list, which was queued from the start. Two
                        // delivery mechanisms in one list biased the very strong-vs-filler comparison the
                        // blend instrumentation exists to make (review F2, 2026-08-25).
                        const isFiller = blendDividerIndex !== null && index >= blendDividerIndex;
                        // 1-based and absolute across pages, so position 21 on page 2 is comparable
                        // with position 21 in an infinite-scroll feed.
                        const position = (page - 1) * pageSize + index + 1;
                        return (
                            <React.Fragment key={getId(item) ?? `row-${index}`}>
                                {/* The zero case is hoisted into the always-mounted live region above,
                                    so only the real separator renders inline. `strongMatchCount === 0`
                                    is the only way `blendDividerIndex` can be 0, so this is the same
                                    partition, not a narrowing. */}
                                {blendDividerIndex === index && strongMatchCount !== 0 ? (
                                    <SearchBlendDivider localizer={localizer} strongMatchCount={strongMatchCount} />
                                ) : null}
                                {isFiller ? (
                                    <div
                                        data-playwright-id="search-filler-row"
                                        onClickCapture={() => queueSearchFillerRowClicked({ entityType: analyticsEntityType })}
                                    >
                                        {renderItem(item, position)}
                                    </div>
                                ) : (
                                    renderItem(item, position)
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            )}

            {cache.errorMessage && visibleItems.length > 0 ? (
                <div className={`rounded-2xl px-4 py-3 text-sm text-muted-foreground ${DESTRUCTIVE_NOTICE_SURFACE_CLASS}`}>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <span>{localizer("search.error.description")}</span>
                        <button
                            type="button"
                            className="text-sm font-medium text-foreground underline-offset-4 hover:underline"
                            onClick={() => {
                                cache.errorMessage = null;
                                void ensurePageLoaded();
                            }}
                        >
                            {localizer("literal.retry")}
                        </button>
                    </div>
                </div>
            ) : null}

            {visibleItems.length > 0 ? (
                <div className="space-y-2">
                    {/* HOW MUCH IS THERE. Eight of eleven review units asked this from four different
                        directions, and it is the one question the page could not answer anywhere: the
                        tab badges say how much each OTHER vertical holds, and the pager says only that
                        another page exists.

                        The number is `strongMatchCount` — REAL matches, not rows on screen. Those
                        differ whenever a sparse page is blend-filled, which is exactly why the count
                        sits above the pager and the blend divider marks the seam below: "12 results"
                        followed by twelve rows, a divider, and then filler is coherent; "20 results"
                        over the same page would not be.

                        Deliberately NOT used to compute a last page and retire the pager's trailing
                        ellipsis. That was the register's suggestion and it is wrong here — the pager
                        walks blended rows, so a last page derived from the strong-match count would
                        under-run the list it is paging. */}
                    {totalResultsLabel ? (
                        <p
                            className="text-center text-sm text-muted-foreground"
                            data-playwright-id="search-total-results"
                        >
                            {totalResultsLabel}
                        </p>
                    ) : null}
                    <SearchPagination
                        currentPage={page}
                        availablePages={availablePages}
                        hasNextPage={hasNextPage}
                        hasPreviousPage={hasPreviousPage}
                        onPageChange={onPageChange}
                        buildPageHref={props.buildPageHref}
                        localizer={localizer}
                    />
                </div>
            ) : null}

            {/* `EMPTY-STATES-65` — `strongMatchCount === 0` prints NOTHING here.

                This line counted rows on screen (`visibleItems.length > 0`) and was blind to
                `strongMatchCount`, the value this component computes one branch earlier to place the
                divider. On a blended zero-match page that made it the third and last claim on a page
                whose first claim was already "nothing matched": "You've reached the end of the
                results for posts", 1,435 CSS px below the sentence denying there were any. No phone
                shows both, so a reader who lands scrolled reads only this one, and its honest reading
                is that six posts matched.

                It now inherits the rule its own sibling has always had: `totalResultsLabel` is `null`
                at zero and refuses to render for exactly this reason.

                `!== 0` rather than a truthiness test, deliberately: `undefined`/`null` means "this
                caller does not report a count" (`/tag/*` and every other hub), and unknown is not
                zero. Those callers keep the marker they have always had. */}
            {!hasNextPage && visibleItems.length > 0 && strongMatchCount !== 0 ? (
                <p className="text-center text-sm text-muted-foreground">
                    {/* `toLocaleLowerCase` with the page's own language tag, not the bare
                        `toLowerCase()` this used to call. The bare form applies the Unicode default
                        mapping, which turns Turkish "I" into "i" instead of "ı" — a standing hazard on
                        a 24-locale catalog, and free to remove (DISCOVERY-27).

                        The English frame was also reworded so the placeholder falls after a
                        preposition, the way every other catalog already had it: interpolating a plural
                        tab label into "the end of {0} results" produced "the end of posts results",
                        two plural nouns in a row. English was the only locale with the problem, so no
                        other catalog changed. */}
                    {localizer(
                        "search.end_of_type_results",
                        resultLabel.toLocaleLowerCase(MetaHandler.getCurrentLanguageTag()),
                    )}
                </p>
            ) : null}
            </div>
        </div>
    );
}

export default SearchPaginatedList;
